import { Injectable, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { EMPTY, Subscription, finalize, switchMap, takeWhile, tap } from 'rxjs';
import { ChatFile, ChatSummary, Message, NEW_CHAT_ID, PipelineProgress, PdfDocumentPreview, TranslationLanguage, translationLanguages, mapStreamMessage } from '../../../core/models/api.models';
import { ChatApiService } from './chat-api.service';
import { parseChatDownloadUrl } from './chat-download-url';
import { ChatStreamService } from './chat-stream.service';
import { SettingsService } from '../../settings/services/settings.service';
import { createAuthorizedChatRequest } from './chat-request';

@Injectable({ providedIn: 'root' })
export class ChatStateService {
  private readonly api = inject(ChatApiService);
  private readonly stream = inject(ChatStreamService);
  private readonly router = inject(Router);
  private readonly i18n = inject(TranslocoService);
  private readonly settings = inject(SettingsService);
  private activeInteraction?: Subscription;
  readonly conversations = signal<ChatSummary[]>([]);
  readonly activeChatId = signal(NEW_CHAT_ID);
  readonly messages = signal<Message[]>([]);
  readonly progress = signal<PipelineProgress | null>(null);
  readonly loading = signal(false);
  readonly sending = signal(false);
  readonly error = signal<string | null>(null);
  readonly pdf = signal<PdfDocumentPreview | null>(null);
  readonly translating = signal(false);
  readonly translationError = signal<string | null>(null);
  readonly hasMessages = computed(() => this.messages().length > 0);

  loadConversations(): void {
    this.api.list().subscribe({
      next: result => this.conversations.set(result.value ?? []),
      error: () => this.error.set(this.i18n.translate('errors.loadConversations')),
    });
  }

  open(id: string): void {
    this.cancelSend();
    this.activeChatId.set(id || NEW_CHAT_ID);
    this.messages.set([]); this.progress.set(null); this.error.set(null);
    if (!id || id === NEW_CHAT_ID) return;
    this.loading.set(true);
    this.api.detail(id).pipe(finalize(() => this.loading.set(false))).subscribe({
      next: result => this.messages.set(result.value?.messages ?? []),
      error: () => this.error.set(this.i18n.translate('errors.loadConversationHistory')),
    });
  }

  newConversation(): void { void this.router.navigate(['/chat', NEW_CHAT_ID]); }
  select(id: string): void { void this.router.navigate(['/chat', id]); }
  delete(id: string): void {
    this.api.delete(id).subscribe({
      next: () => { this.conversations.update(items => items.filter(item => item.id !== id)); if (id === this.activeChatId()) this.newConversation(); },
      error: () => this.error.set(this.i18n.translate('errors.deleteConversation')),
    });
  }

  send(text: string): void {
    const clean = text.trim().slice(0, 500);
    const request = createAuthorizedChatRequest(this.activeChatId(), clean, this.settings.selectedKnowledgeSources(), this.settings.availableKnowledgeSources());
    if (!clean || this.sending() || !request) return;
    const temporaryUserId = `user-${Date.now()}`;
    const temporaryAssistantId = `streaming-${Date.now()}`;
    this.messages.update(items => [...items, { id: temporaryUserId, text: clean, user: 'Você', date: new Date().toISOString(), userMessage: true, dislike: false, messageFiles: [] }]);
    this.sending.set(true); this.error.set(null); this.progress.set(null);
    let receivedFinal = false;
    const interaction = new Subscription();
    this.activeInteraction = interaction;
    interaction.add(this.stream.stream(request).pipe(
      tap(event => { if (event.type === 'error') throw new Error('Chat processing failed.'); }),
      takeWhile(event => event.type !== 'final', true),
      finalize(() => {
        this.sending.set(false);
        this.progress.set(null);
        if (!receivedFinal) this.messages.update(items => items.filter(item => item.id !== temporaryAssistantId));
        if (this.activeInteraction === interaction) this.activeInteraction = undefined;
      }),
    ).subscribe({
      next: event => {
        if (event.type === 'progress') this.progress.set({ step: event.step, message: event.message, order: event.order });
        if (event.type === 'token' && event.message) this.appendToken(temporaryAssistantId, event.message);
        if (event.type === 'final') {
          receivedFinal = true;
          const definitive = [mapStreamMessage(event.data.Asc), mapStreamMessage(event.data.Message)];
          this.messages.update(items => [...items.filter(item => item.id !== temporaryUserId && item.id !== temporaryAssistantId), ...definitive]);
          this.progress.set(null);
          if (this.activeChatId() === NEW_CHAT_ID && event.data.FolderId) {
            this.activeChatId.set(event.data.FolderId);
            void this.router.navigate(['/chat', event.data.FolderId], { replaceUrl: true });
          }
        }
      },
      error: () => this.error.set(this.i18n.translate('errors.interruptedResponse')),
      complete: () => {
        if (receivedFinal) this.loadConversations();
        else this.error.set(this.i18n.translate('errors.interruptedResponse'));
      },
    }));
  }

  cancelSend(): void { this.activeInteraction?.unsubscribe(); }

  react(message: Message, reaction: 1 | 2): void {
    const request = reaction === 1 ? this.api.like(this.activeChatId(), message.id) : this.api.dislike(this.activeChatId(), message.id);
    request.subscribe({
      next: () => this.messages.update(items => items.map(item => item.id === message.id ? { ...item, userReaction: item.userReaction === reaction ? 0 : reaction } : item)),
      error: () => this.error.set(this.i18n.translate('errors.saveFeedback')),
    });
  }

  preview(file: ChatFile): void {
    const chatId = this.activeChatId();
    // The same Search reference can occur in several SSE answers with the same id.
    const messageId = this.messages().find(message => message.messageFiles.includes(file))?.id;
    const translationContext = messageId && file.chunkId && file.documentId && chatId !== NEW_CHAT_ID
      ? { chatId, messageId, chunkId: file.chunkId, documentId: file.documentId } : undefined;
    this.translationError.set(null);
    this.loading.set(true);
    this.api.download(file.chunkId, file.documentId).pipe(finalize(() => this.loading.set(false))).subscribe({
      next: result => {
        const url = result.success ? parseChatDownloadUrl(result.value) : null;
        if (url) this.pdf.set({ url, title: file.displayName || 'Preview PDF', displayName: file.displayName ?? null, translationContext });
        else this.error.set(this.i18n.translate('errors.loadPdf'));
      },
      error: () => this.error.set(this.i18n.translate('errors.loadPdf')),
    });
  }

  translateDocument(languageTo: TranslationLanguage): void {
    const document = this.pdf();
    if (!document?.translationContext || this.translating() || !translationLanguages.some(language => language.value === languageTo)) return;
    this.translating.set(true);
    this.translationError.set(null);
    const { chatId, messageId, chunkId, documentId } = document.translationContext;
    // SSE Id can be a Search ID. Only the persisted message file from history
    // is authoritative for MessageFileId; never fall back to reference IDs.
    this.api.detail(chatId).pipe(
      switchMap(result => {
        if (this.pdf() !== document) return EMPTY;
        const message = result.value?.messages?.find(item => item.id === messageId);
        const files = message?.messageFiles?.filter(file => file.chunkId === chunkId && file.documentId === documentId) ?? [];
        const messageFile = files.length === 1 ? files[0] : undefined;
        if (result.success === false || !messageFile?.id?.trim() || messageFile.id === messageFile.chunkId) {
          throw new Error('Persisted message file was not found.');
        }
        return this.api.translateDocument({ chatId, messageId, messageFileId: messageFile.id, languageTo });
      }),
      finalize(() => this.translating.set(false)),
    ).subscribe({
        next: result => {
          if (this.pdf() !== document) return;
          const url = result.success !== false ? parseChatDownloadUrl(result.value) : null;
          if (url) {
            const language = translationLanguages.find(item => item.value === languageTo)!;
            this.pdf.set({ ...document, url, title: `${document.title} (${language.label})` });
          } else this.translationError.set(this.i18n.translate('errors.translateDocument'));
        },
        error: () => {
          if (this.pdf() === document) this.translationError.set(this.i18n.translate('errors.translateDocument'));
        },
      });
  }

  closePdf(): void { this.pdf.set(null); this.translationError.set(null); }
  clearError(): void { this.error.set(null); }

  private appendToken(id: string, token: string): void {
    this.messages.update(items => {
      const index = items.findIndex(item => item.id === id);
      if (index < 0) return [...items, { id, text: token, user: 'TAI', date: new Date().toISOString(), userMessage: false, dislike: false, messageFiles: [], streaming: true }];
      return items.map(item => item.id === id ? { ...item, text: item.text + token } : item);
    });
  }
}
